import { appConfig } from '../config/appConfig';
import type { KnowledgeParticle, PaginationResponse } from '../types/domain';
import type { PapFailure, PrivateActivityPackage } from './ttqService';
import { apiClient } from './apiClient';

export type KnowledgeParticleOverlayExtractView = {
  particleId: string;
  eligible: boolean;
  coachOverlay: Record<string, unknown> | null;
  learnerEvidence: Record<string, unknown> | null;
  coachOverlayStatus: string | null;
  learnerEvidenceStatus: string | null;
  coachOverlayError: string | null;
  learnerEvidenceError: string | null;
  imageOssKey: string | null;
};

export type ParticlePapExtractionStatus = 'pending' | 'running' | 'failed' | 'ready' | 'partial';

export type KnowledgeParticlePapView = {
  particleId: string;
  eligible: boolean;
  subjectCode: string | null;
  extractionStatus: ParticlePapExtractionStatus;
  pap: PrivateActivityPackage | null;
  failure: PapFailure | null;
  updatedAt: string | null;
};

export type SubjectValueBindingOptions = {
  particleId: string;
  subject?: string | null;
  runtimeStatus: 'ready' | 'unavailable' | 'empty';
  runtimeMessage: string | null;
  profiles: Array<{
    profileId: string;
    displayName?: string;
    subjectCode?: string;
    subjectLabel?: string;
    nodes: Array<{
      spineId: string;
      spineTitle?: string;
      nodeId: string;
      nodeTitle?: string;
      nodeNo: number;
      moduleKey?: string;
      moduleLabel?: string;
    }>;
  }>;
  current: Record<string, unknown> | null;
};

export const knowledgeService = {
  async listKnowledgeParticles(input: {
    userId: string;
    page?: number;
    pageSize?: number;
    search?: string;
    subject?: string;
    sort?: 'created_at_desc' | 'created_at_asc' | 'updated_at_desc' | 'updated_at_asc';
    ttqUnusedOnly?: boolean;
  }): Promise<PaginationResponse<KnowledgeParticle>> {
    const params = new URLSearchParams({
      userId: input.userId,
      page: String(input.page ?? 1),
      pageSize: String(input.pageSize ?? 20),
      search: input.search ?? '',
      includeStats: 'false',
      unlinkedOnly: 'false',
    });
    if (input.subject) params.set('subject', input.subject);
    if (input.sort) params.set('sort', input.sort);
    if (input.ttqUnusedOnly) params.set('ttqUnusedOnly', 'true');
    return apiClient.getEnvelope<PaginationResponse<KnowledgeParticle>>(`/api/knowledge-particles?${params}`);
  },

  async listNoSpineBindingParticles(input: {
    userId: string;
    limit?: number;
    offset?: number;
    subject?: string | null;
  }): Promise<{
    total: number;
    particles: Array<{
      id: string;
      user_id: string;
      title: string | null;
      subject: string | null;
      created_at: string;
      binding_status: string;
      enrichment_status: string | null;
      enrichment_progress_label: string | null;
      spine_node_title: string | null;
      auto_retry_count: number;
      question_preview: string | null;
    }>;
    pagination: { limit: number; offset: number };
  }> {
    const params = new URLSearchParams();
    params.set('userId', input.userId);
    if (input.limit != null) params.set('limit', String(input.limit));
    if (input.offset != null) params.set('offset', String(input.offset));
    if (input.subject) params.set('subject', input.subject);
    return apiClient.getEnvelope(`/api/knowledge-particles/no-spine-binding?${params.toString()}`);
  },

  async getKnowledgeParticle(particleId: string): Promise<KnowledgeParticle> {
    return apiClient.getEnvelope<KnowledgeParticle>(`/api/knowledge-particles/${particleId}?backfill=none`);
  },

  async getKnowledgeParticlesByIds(particleIds: string[]): Promise<KnowledgeParticle[]> {
    const ids = [...new Set(particleIds.map((id) => String(id || '').trim()).filter(Boolean))];
    if (!ids.length) {
      return [];
    }
    return apiClient.postEnvelope<KnowledgeParticle[]>('/api/knowledge-particles/batch', {
      particleIds: ids,
    }, true);
  },

  async getParticlePap(particleId: string, options?: { kickoff?: boolean }): Promise<KnowledgeParticlePapView> {
    const params = new URLSearchParams();
    if (options?.kickoff === false) {
      params.set('kickoff', 'none');
    }
    const query = params.toString();
    return apiClient.getEnvelope<KnowledgeParticlePapView>(
      `/api/knowledge-particles/${particleId}/pap${query ? `?${query}` : ''}`,
    );
  },

  /** 用户主动（重新）提取 PAP；force=true 表示不满意后的重建。 */
  async extractParticlePap(particleId: string, input?: { force?: boolean; wait?: boolean }): Promise<KnowledgeParticlePapView> {
    return apiClient.postEnvelope<KnowledgeParticlePapView>(
      `/api/knowledge-particles/${particleId}/pap/extract`,
      { force: input?.force ?? true, wait: input?.wait ?? false },
    );
  },

  async getKnowledgeParticleOverlays(
    particleId: string,
    options?: { userId?: string; kickoff?: boolean },
  ): Promise<KnowledgeParticleOverlayExtractView> {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.kickoff) params.set('kickoff', 'true');
    const query = params.toString();
    return apiClient.getEnvelope<KnowledgeParticleOverlayExtractView>(
      `/api/knowledge-particles/${encodeURIComponent(particleId)}/overlays${query ? `?${query}` : ''}`,
    );
  },

  async extractKnowledgeParticleOverlays(
    particleId: string,
    options: {
      userId: string;
      force?: boolean;
      wait?: boolean;
      layers?: 'coach' | 'learner' | 'both';
    },
  ): Promise<KnowledgeParticleOverlayExtractView> {
    return apiClient.postEnvelope<KnowledgeParticleOverlayExtractView>(
      `/api/knowledge-particles/${encodeURIComponent(particleId)}/overlays/extract`,
      {
        userId: options.userId,
        force: options.force !== false,
        wait: options.wait !== false,
        layers: options.layers || 'both',
      },
      true,
      { timeoutMs: appConfig.papExtractTimeoutMs },
    );
  },

  async updateKnowledgeParticle(
    particleId: string,
    updates: Partial<Pick<KnowledgeParticle, 'title' | 'questions' | 'answers' | 'tags' | 'status' | 'metadata'>>,
  ): Promise<KnowledgeParticle> {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: KnowledgeParticle; error?: string }>(
      `/api/knowledge-particles/${particleId}/update`,
      { method: 'POST', body: updates, auth: true },
    );
    if (!payload.success || !payload.data) throw new Error(payload.error || '更新失败');
    return payload.data;
  },

  async deleteKnowledgeParticle(particleId: string): Promise<void> {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: unknown;
      error?: string;
      message?: string;
      code?: string;
    }>(`/api/knowledge-particles/${particleId}/delete`, {
      method: 'POST',
      auth: true,
    });

    if (!payload.success) {
      throw new Error(payload.error || payload.message || '删除知识颗粒失败');
    }

    if (payload.data === false && payload.code === 'ASSET_EXTRACTING_LOCKED') {
      throw new Error(payload.message || '该知识颗粒关联的素材正在提取中，暂不可删除');
    }
  },

  async listSubjectValueBindingOptions(particleId: string): Promise<SubjectValueBindingOptions> {
    return apiClient.getEnvelope<SubjectValueBindingOptions>(
      `/api/knowledge-particles/${encodeURIComponent(particleId)}/subject-value-binding/options`,
    );
  },

  async putSubjectValueBinding(
    particleId: string,
    body: {
      primary?: {
        subjectValueProfileId: string;
        spineId: string;
        spineNodeId: string;
        reason?: string;
      } | null;
      secondary?: {
        subjectValueProfileId: string;
        spineId: string;
        spineNodeId: string;
        reason?: string;
      } | null;
    },
  ): Promise<KnowledgeParticle> {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: KnowledgeParticle;
      error?: string;
      message?: string;
    }>(`/api/knowledge-particles/${encodeURIComponent(particleId)}/subject-value-binding`, {
      method: 'PUT',
      body,
      auth: true,
    });

    if (!payload.success || !payload.data) {
      throw new Error(payload.error || payload.message || '更新学科主干绑定失败');
    }

    return payload.data;
  },

  async backfillKnowledgeParticleBinding(
    particleId: string,
    options: { force?: boolean; wait?: boolean } = {},
  ): Promise<KnowledgeParticle | { particleId: string; status: string; queue?: Record<string, unknown> }> {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: KnowledgeParticle | { particleId: string; status: string; queue?: Record<string, unknown> };
      error?: string;
      message?: string;
    }>(`/api/knowledge-particles/${encodeURIComponent(particleId)}/revision-family-enrichment/backfill`, {
      method: 'POST',
      body: {
        force: options.force ?? true,
        wait: options.wait ?? false,
      },
      auth: true,
    });

    if (!payload.success || !payload.data) {
      throw new Error(payload.error || payload.message || '重新绑定学科主干失败');
    }

    return payload.data;
  },

  async applyKnowledgeParticleQaPatch(
    particleId: string,
    input: {
      changeScope?: 'answer_only' | 'question_and_answer' | 'format_only';
      summary?: string;
      questions?: unknown;
      answers?: unknown;
      threadId?: string;
    },
  ): Promise<KnowledgeParticle> {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: KnowledgeParticle; error?: string; message?: string }>(
      `/api/knowledge-particles/${particleId}/apply-qa-patch`,
      { method: 'POST', body: input, auth: true },
    );
    if (!payload.success || !payload.data) {
      throw new Error(payload.error || payload.message || '写回知识颗粒失败');
    }
    return payload.data;
  },

  /**
   * AI 建议缩略式：专用接口直接返回可渲染的 `$...$`，不做旁白提取。
   * 服务端固定 DeepSeek V4 Pro。
   */
  async suggestAbbrExpr(
    particleId: string,
  ): Promise<{ abbrExpr: string; model?: string; provider?: string }> {
    return apiClient.postEnvelope<{ abbrExpr: string; model: string; provider: string }>(
      `/api/knowledge-particles/${particleId}/abbr-expr/suggest`,
      {},
      true,
      { timeoutMs: 180_000 },
    );
  },
};
